import {
  ArrowsRightLeftIcon,
  CheckIcon,
  LinkIcon,
  QrCodeIcon
} from '@heroicons/react/24/solid'
import clsx from 'clsx'
import PropTypes from 'prop-types'

import { CROSS_JOBS, CROSS_TALENTS, FIT_LABELS } from '../content'
import { SubRange } from '../engine'
import { anim, prog } from '../motion'
import { COLORS, Shape } from '../primitives'
import { useBeats } from '../v3/beats'
import { Panel } from '../v3/panels'
import { Radar } from '../v7/extras/team'
import { de } from './copy'

// Gestaffelte Bereiche als Anteile der Station, nicht der ganzen Section
const stOf = (inB) => (beat, start, step, len, i) =>
  inB(beat, start + step * i, start + step * i + len)

/**
 * Produkt-Elemente der Version 9, die es in v3 noch nicht gibt. Gestaltung
 * angelehnt an Dashboard und PDF-Reports, Zahlen sind Beispielwerte.
 * Die Zeitleiste kommt über `BeatsProvider`, so laufen sie auch in V10.
 */

const fitOf = (v) => (v > 70 ? 'high' : v > 40 ? 'mid' : 'low')

// ---------- Öffentlicher Einladungslink ----------

const CHANNELS = [
  'Stellenanzeige',
  'Karriereseite',
  'Social Media',
  'Messe & Events'
]

export function LinkMock() {
  const { inB } = useBeats()
  const st = stOf(inB)
  return (
    <Panel
      at={inB('link', 0.06, 0.18)}
      out={inB('link', 0.9, 1)}
      className='v9-card v9-link'
    >
      <div className='v9-card__eyebrow'>Öffentlicher Einladungslink</div>
      <div className='v9-card__title'>Customer Success Manager:in</div>
      <div className='v9-link__row'>
        <span className='v9-link__url'>
          <LinkIcon width={14} height={14} />
          <i />
        </span>
        <span className='v9-link__btn'>Link kopieren</span>
      </div>
      <div className='v9-link__body'>
        <div className='v9-link__qr'>
          <QrCodeIcon width={92} height={92} />
          <small>QR-Code herunterladen</small>
        </div>
        <div className='v9-link__channels'>
          {CHANNELS.map((c, i) => (
            <span
              key={c}
              style={anim({
                at: st('link', 0.2, 0.05, 0.1, i),
                x: 16,
                y: 0
              })}
            >
              <CheckIcon width={12} height={12} /> {c}
            </span>
          ))}
        </div>
      </div>
      <div
        className='v9-attract'
        style={anim({ at: inB('link', 0.5, 0.62), y: 16 })}
      >
        <small>Aivy® Attract · so sehen es Talente</small>
        <div className='v9-attract__match'>
          <em>Passt sehr gut</em>
          <b>Customer Success Manager:in</b>
          <span>
            <i style={{ width: '91%' }} />
          </span>
        </div>
      </div>
    </Panel>
  )
}

// ---------- Talentliste, sortiert nach Passung ----------

const TALENTS = [
  { name: 'Lena K.', value: 91, color: 'pink' },
  { name: 'Aylin S.', value: 86, color: 'yellow' },
  { name: 'Mo A.', value: 82, color: 'green' },
  { name: 'Tom B.', value: 66, color: '#B2B4B6' },
  { name: 'Jonas M.', value: 62, color: '#4461D3' },
  { name: 'Sara L.', value: 58, color: '#C9A7F2' },
  { name: 'Ben K.', value: 47, color: '#7FD3E6' },
  { name: 'Nina P.', value: 39, color: '#F2B48F' }
]

export function TalentList({ apps }) {
  const { inB } = useBeats()
  const st = stOf(inB)
  return (
    <Panel
      at={inB('liste', 0.04, 0.16)}
      out={inB('liste', 0.9, 1)}
      className='v9-card v9-list'
    >
      <div className='v9-list__head'>
        <div>
          <div className='v9-card__eyebrow'>Talente</div>
          <div className='v9-card__title'>Customer Success Manager:in</div>
        </div>
        <span className='v9-list__count'>{de(apps)} Bewerbungen</span>
      </div>
      <div className='v9-list__chips'>
        <span>Assessment abgeschlossen</span>
        <span className='is-on'>Sortiert nach Passung</span>
      </div>
      {TALENTS.map((t, i) => {
        const fit = fitOf(t.value)
        return (
          <div
            key={t.name}
            className={clsx('v9-list__row', i < 3 && 'is-top')}
            style={anim({ at: st('liste', 0.14, 0.035, 0.08, i), x: 20, y: 0 })}
          >
            <i
              className='v9-avatar'
              style={{ background: COLORS[t.color] || t.color }}
            >
              {t.name[0]}
            </i>
            <b>{t.name}</b>
            <span className='v9-list__bar'>
              <i
                style={{
                  ...prog(st('liste', 0.18, 0.035, 0.12, i)),
                  width: `${t.value}%`,
                  transform: 'scaleX(var(--p))'
                }}
              />
            </span>
            <strong>{t.value}</strong>
            <em className={`v9-fit v9-fit--${fit}`}>{FIT_LABELS[fit]}</em>
          </div>
        )
      })}
      <div
        className='v9-list__hint'
        style={anim({ at: inB('liste', 0.6, 0.7), y: 10 })}
      >
        Die Top 3 bekommen ein ausführliches Passungsprofil.
      </div>
    </Panel>
  )
}

TalentList.propTypes = { apps: PropTypes.number.isRequired }

// ---------- Teamanalyse ----------

export function TeamMock() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('team', 0.04, 0.14)}
      out={inB('team', 0.9, 1)}
      className='v9-card v9-team'
    >
      <div className='v9-card__eyebrow'>Teamanalyse</div>
      <div className='v9-card__title'>Customer Success</div>
      <SubRange at={inB('team', 0.05, 0.95)}>
        <Radar />
        <div className='kx-legend'>
          <span>
            <i style={{ background: COLORS.blue }} /> Team
          </span>
          <span style={{ ...prog([0.28, 0.34]), opacity: 'var(--p)' }}>
            <i className='is-dash' /> Stellenanforderung
          </span>
          <span style={{ ...prog([0.48, 0.54]), opacity: 'var(--p)' }}>
            <i style={{ background: COLORS.pink }} /> Lena K.
          </span>
        </div>
      </SubRange>
    </Panel>
  )
}

// ---------- Kreuzmatching ----------

const PALE = [235, 239, 253]
const BLUE = [64, 123, 255]
const heat = (v) => {
  const t = Math.min(1, Math.max(0, (v - 50) / 45))
  return `rgb(${PALE.map((c, i) => Math.round(c + (BLUE[i] - c) * t)).join(',')})`
}
// Lena passt am besten zu Customer Success, Jonas zum Vertrieb
const BEST = [
  [0, 1],
  [1, 0]
]

export function CrossMock() {
  const { inB } = useBeats()
  return (
    <Panel
      at={inB('kreuz', 0.04, 0.14)}
      out={inB('kreuz', 0.9, 1)}
      className='v9-card v9-cross'
    >
      <div className='v9-card__eyebrow'>
        <ArrowsRightLeftIcon width={13} height={13} /> Kreuzmatching
      </div>
      <div className='v9-card__title'>Ein Assessment, alle offenen Stellen</div>
      <div
        className='v9-cross__grid'
        style={{
          gridTemplateColumns: `110px repeat(${CROSS_JOBS.length}, 1fr)`
        }}
      >
        <span />
        {CROSS_JOBS.map((j) => (
          <span key={j} className='v9-cross__job'>
            {j}
          </span>
        ))}
        {CROSS_TALENTS.map((t, r) => (
          <Row key={t.name} t={t} r={r} />
        ))}
      </div>
      <div
        className='v9-cross__note'
        style={anim({ at: inB('kreuz', 0.55, 0.65), y: 10 })}
      >
        <b>Lena</b> passt am besten zu Customer Success, <b>Jonas</b> zum
        Vertrieb.
      </div>
    </Panel>
  )
}

function Row({ t, r }) {
  const { inB } = useBeats()
  const st = stOf(inB)
  return (
    <>
      <span className='v9-cross__name'>{t.name}</span>
      {t.values.map((v, c) => {
        const best = BEST.some(([br, bc]) => br === r && bc === c)
        return (
          <span
            key={c}
            className='v9-cross__cell'
            style={{
              background: heat(v),
              color: v > 78 ? '#fff' : COLORS.ink,
              ...anim({
                at: st('kreuz', 0.16, 0.015, 0.06, r * 4 + c),
                s: 0.6,
                y: 0
              })
            }}
          >
            {v}&nbsp;%
            {best && (
              <i
                className='v9-cross__ring'
                style={anim({ at: inB('kreuz', 0.46, 0.54), s: 1.3, y: 0 })}
              />
            )}
          </span>
        )
      })}
    </>
  )
}

Row.propTypes = {
  t: PropTypes.object.isRequired,
  r: PropTypes.number.isRequired
}

// ---------- Zierformen im Hintergrund der Bühne ----------

export function StageShapes() {
  return (
    <div className='v9-shapes' aria-hidden='true'>
      <span style={{ left: '6%', top: '10%' }}>
        <Shape kind='tri' color='yellow' size={70} rotate={-12} />
      </span>
      <span style={{ right: '8%', bottom: '12%' }}>
        <Shape kind='dot' color='pink' size={54} />
      </span>
      <span style={{ right: '14%', top: '8%' }}>
        <Shape kind='sq' color='green' size={40} rotate={14} />
      </span>
    </div>
  )
}
